import { PublicClientApplication } from "@azure/msal-browser";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { ThemeProvider } from "@mui/material";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";
import { config as zodConfig } from "zod";

import { type ApiCoreConfig, ApiCoreConfigProvider } from "@dfp/react-api-core";
import { reactPlugin } from "@dfp/react-app-insights";
import { standardTheme } from "@dfp/react-mui-theme";
import { ReplicatedHistoryProvider } from "@dfp/react-replicated-history";

import App from "./App.tsx";
import { makeMsalConfig } from "./auth/makeMsalConfig.ts";
import { makeTokenRequestObject } from "./auth/makeTokenRequestObject.ts";
import {
  API_BASE_URI,
  API_ENDPOINT_PREFIX,
  API_FX_BASE_URI,
  API_FX_ENDPOINT_PREFIX,
  API_FX_SUBSCRIPTION_KEY,
  API_SUBSCRIPTION_KEY,
  DEMO_MODE,
} from "./config.ts";
import {
  DemoPasswordGate,
  demoMsalAccount,
  initDemoOnboarding,
  installDemoMockAxios,
  installDemoPdfWorker,
  startDemoOnboarding,
} from "./demo/index.ts";
import "./i18n/initI18next.ts";
import "./index.css";

if (DEMO_MODE) {
  // Must run before the first getUserContext request — clears stale/abandoned
  // onboarding flags so a returning visitor gets the dashboard, not a locked
  // onboarding (see demoOnboarding.ts).
  initDemoOnboarding();
  installDemoMockAxios();
  installDemoPdfWorker();
}

// Disable zod's JIT parser: it compiles validators via `new Function`, which
// our CSP (no 'unsafe-eval') blocks. Falls back to the interpreted parser.
zodConfig({ jitless: true });
const apiCoreConfig: ApiCoreConfig = {
  providers: {
    bw: {
      apiBaseUri: API_BASE_URI,
      apiSubscriptionKey: API_SUBSCRIPTION_KEY,
      apiEndpointPrefix: API_ENDPOINT_PREFIX,
    },
    fx: {
      apiBaseUri: API_FX_BASE_URI,
      apiSubscriptionKey: API_FX_SUBSCRIPTION_KEY,
      apiEndpointPrefix: API_FX_ENDPOINT_PREFIX,
    },
  },
  makeTokenRequestObject,
};

function renderApp(msalInstance: PublicClientApplication) {
  const container = document.getElementById("root");

  if (!container) {
    throw new Error("DOM root container not found");
  }

  const root = ReactDOM.createRoot(container);
  const tree = (
    <React.StrictMode>
      <AppInsightsContext.Provider value={reactPlugin}>
        <ApiCoreConfigProvider config={apiCoreConfig}>
          <ThemeProvider theme={standardTheme}>
            <BrowserRouter>
              <NuqsAdapter>
                <ReplicatedHistoryProvider>
                  <App pca={msalInstance} />
                </ReplicatedHistoryProvider>
              </NuqsAdapter>
            </BrowserRouter>
          </ThemeProvider>
        </ApiCoreConfigProvider>
      </AppInsightsContext.Provider>
    </React.StrictMode>
  );
  // In demo mode the public preview URL sits behind a soft password gate so
  // casual visitors can't see the fixtures.
  root.render(DEMO_MODE ? <DemoPasswordGate>{tree}</DemoPasswordGate> : tree);
}

function initializeMsalInstance(msalInstance: PublicClientApplication) {
  msalInstance.initialize().then(() => {
    // Default to using the first account if no account is active on page load
    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      // Account selection logic is app dependent. Adjust as needed for different use cases.
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]); //
    }

    renderApp(msalInstance);
  });
}

if (DEMO_MODE) {
  // Demo mode: stub the MSAL instance so any code that still calls into it
  // (e.g. useBareApi requesting an access token before each API call) gets
  // a synthetic account + token. The real network call is then short-circuited
  // by the demo axios adapter.

  // Clear MSAL-related sessionStorage from any previous demo session — stale
  // state (cached redirect URIs, pending login requests, etc.) makes
  // initialize() hang silently on Safari after Cmd+R.
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (/^msal\.|^\{"authority":/i.test(key)) {
        window.sessionStorage.removeItem(key);
      }
    }
  } catch {
    // Private mode or storage disabled — ignore.
  }

  const stubInstance = new PublicClientApplication({
    auth: { clientId: "00000000-0000-0000-0000-000000000000" },
  });
  stubInstance.getActiveAccount = () => demoMsalAccount;
  stubInstance.getAllAccounts = () => [demoMsalAccount];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).acquireTokenSilent = async () => ({
    accessToken: "demo-access-token",
    account: demoMsalAccount,
    idToken: "demo-id-token",
    idTokenClaims: demoMsalAccount.idTokenClaims,
    scopes: [],
    tokenType: "Bearer",
    uniqueId: demoMsalAccount.localAccountId,
    tenantId: demoMsalAccount.tenantId,
    expiresOn: new Date(Date.now() + 60 * 60 * 1000),
    extExpiresOn: new Date(Date.now() + 60 * 60 * 1000),
    fromCache: true,
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).acquireTokenRedirect = async () => undefined;
  // The real initialize() does a redirect-promise round trip that occasionally
  // never settles in a Vercel-protected deployment. Replace with a synchronously
  // resolved promise so MsalProvider can move out of "startup" immediately.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).initialize = async () => undefined;
  // MsalProvider keeps `inProgress` at "startup" until handleRedirectPromise
  // resolves. On a Vercel preview that promise occasionally never settles,
  // leaving useBareApi to return new Promise(() => undefined) for every
  // request. Resolve it synchronously so inProgress flips to "none" on the
  // first render tick.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).handleRedirectPromise = async () => null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).loginRedirect = async () => undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).ssoSilent = async () => ({
    accessToken: "demo-access-token",
    account: demoMsalAccount,
    idToken: "demo-id-token",
    idTokenClaims: demoMsalAccount.idTokenClaims,
    scopes: [],
    tokenType: "Bearer",
    uniqueId: demoMsalAccount.localAccountId,
    tenantId: demoMsalAccount.tenantId,
    expiresOn: new Date(Date.now() + 60 * 60 * 1000),
    extExpiresOn: new Date(Date.now() + 60 * 60 * 1000),
    fromCache: true,
  });

  // Demo logout: there's no real B2C session to end. Instead, enter the
  // onboarding flow — set the onboarding flag (so getUserContext returns a
  // company-less user) and hard-navigate to the join entry. The role guard then
  // lands on /join/company-lookup; creating a company clears the flag and the
  // flow returns to the dashboard. (useLogout calls instance.logout().)
  const enterDemoOnboarding = async () => {
    startDemoOnboarding();
    window.location.assign("/join/company-lookup");
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).logout = enterDemoOnboarding;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).logoutRedirect = enterDemoOnboarding;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (stubInstance as any).logoutPopup = enterDemoOnboarding;

  // Belt-and-suspenders: race initialize against a 1.5s timeout so a hung
  // MSAL never blocks the splash screen.
  let rendered = false;
  const renderOnce = () => {
    if (rendered) return;
    rendered = true;
    renderApp(stubInstance);
  };
  stubInstance
    .initialize()
    .catch(() => undefined)
    .finally(renderOnce);
  setTimeout(renderOnce, 1500);
} else {
  const msalInstance = new PublicClientApplication(makeMsalConfig());
  initializeMsalInstance(msalInstance);
}
